import React, { createContext, useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "./api";
import { OnboardingBanner } from "./pages/PortalOnboarding.jsx";

// Page permissions for office staff accounts. The server (server/lib/access.js)
// enforces them; this only shapes the menu and shows a friendly message.
const AccessContext = createContext({ ready: true, full: true, permissions: {}, registry: null, caregiver: false });

export function AccessProvider({ user, children }) {
  const [state, setState] = useState({ ready: user?.role !== "staff", full: user?.role !== "staff", permissions: {}, registry: null });
  useEffect(() => {
    if (user?.role !== "staff") { setState({ ready: true, full: true, permissions: {}, registry: null }); return; }
    api.access.me()
      .then((d) => setState({ ready: true, full: false, permissions: d.permissions || {}, registry: d.registry, caregiver: Boolean(d.also_caregiver) }))
      .catch(() => setState({ ready: true, full: false, permissions: {}, registry: { departments: [], pages: [] } }));
  }, [user?.id, user?.role]);
  return <AccessContext.Provider value={state}>{children}</AccessContext.Provider>;
}

export const useAccess = () => useContext(AccessContext);

const under = (path, base) => path === base || path.startsWith(base + "/");

/** The registry page that covers a client path, or null. */
export function pageForPath(registry, path) {
  if (!registry) return null;
  let best = null;
  for (const p of registry.pages) {
    for (const base of p.paths) if (under(path, base) && (!best || base.length > best.len)) best = { page: p, len: base.length };
    for (const ex of p.exact || []) if (path === ex && (!best || ex.length > best.len)) best = { page: p, len: ex.length };
  }
  return best?.page || null;
}

const DEPT_HOMES = { "/admin/care": "care", "/admin/quality-hub": "quality", "/admin/hr": null, "/admin/finance": "finance", "/admin/growth": "growth", "/admin/administration": "administration" };

export function canSeePath(access, path) {
  if (access.full) return true;
  if (path === "/") return true;
  if (under(path, "/portal/onboarding")) return true; // every staff member completes their own new hire packet
  if (access.caregiver && (under(path, "/portal") || under(path, "/courses"))) return true; // the caregiver hat
  if (Object.prototype.hasOwnProperty.call(DEPT_HOMES, path) && DEPT_HOMES[path]) {
    return access.registry?.pages.some((p) => p.dept === DEPT_HOMES[path] && access.permissions[p.key]);
  }
  const page = pageForPath(access.registry, path);
  return Boolean(page && access.permissions[page.key]);
}

export function levelForPath(access, path) {
  if (access.full) return "edit";
  const page = pageForPath(access.registry, path);
  return page ? access.permissions[page.key] || null : null;
}

/** Wraps the admin area for staff: blocks pages they were not given, and labels view-only pages. */
export function StaffGuard({ children }) {
  const access = useAccess();
  const { pathname } = useLocation();
  if (access.full) return children;
  if (!access.ready) return <div className="text-gray-500">Loading…</div>;
  if (!canSeePath(access, pathname)) return (
    <div className="max-w-xl mx-auto bg-white border rounded-xl p-6 text-center mt-6">
      <h1 className="text-xl font-bold" style={{ color: "#154E93" }}>You don't have access to this page</h1>
      <p className="text-sm text-gray-600 mt-2">Ask an administrator to grant access under Administration → Users &amp; Access.</p>
      <Link to="/" className="inline-block mt-4 px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: "#154E93" }}>Go to my pages</Link>
    </div>
  );
  const level = under(pathname, "/portal") || under(pathname, "/courses") ? null : levelForPath(access, pathname);
  return (
    <>
      {level === "view" && <div className="mb-3 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">View-only access: you can look but not change anything on this page.</div>}
      {children}
    </>
  );
}

/** Home screen for staff: the pages they can open, grouped by department. */
export function StaffHome({ user }) {
  const access = useAccess();
  if (!access.ready || !access.registry) return <div className="text-gray-500">Loading…</div>;
  const mine = access.registry.pages.filter((p) => access.permissions[p.key]);
  const caregiverLinks = [["My Schedule", "/portal/schedule"], ["EVV / Clock In", "/portal/evv"], ["Mobile Portal", "/portal/mobile"], ["Timesheets", "/portal/timesheets"],
    ["Mileage", "/portal/mileage"], ["Training", "/portal/training"], ["My Compliance", "/portal/compliance"], ["Messages", "/portal/communications"], ["My New Hire Packet", "/portal/onboarding"]];
  return (
    <div className="max-w-5xl mx-auto space-y-5">
      <section className="rounded-2xl p-6 text-white shadow" style={{ background: "linear-gradient(120deg,#0f3d73,#154E93 58%,#2870b8)" }}>
        <div className="text-blue-100 text-sm">Welcome</div>
        <h1 className="text-2xl font-bold">{user.name}</h1>
        <p className="text-blue-100 text-sm mt-1">You have access to {mine.length} office page{mine.length === 1 ? "" : "s"}{access.caregiver ? " and your caregiver portal" : ""}.</p>
      </section>
      <OnboardingBanner />
      {access.caregiver && (
        <section className="bg-white border-2 rounded-xl p-4" style={{ borderColor: "#C9A84C" }}>
          <h2 className="font-bold mb-1" style={{ color: "#154E93" }}>My caregiver work</h2>
          <p className="text-xs text-gray-500 mb-3">Your own shifts, visits and time — the same screens every caregiver uses.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {caregiverLinks.map(([label, to]) => <Link key={to} to={to} className="border rounded-lg p-3 text-sm font-semibold text-gray-800 hover:bg-slate-50">{label}</Link>)}
          </div>
        </section>
      )}
      {mine.length === 0 && <div className="bg-white border rounded-xl p-5 text-sm text-gray-600">No pages have been assigned to your account yet. Ask an administrator.</div>}
      {access.registry.departments.map((d) => {
        const pages = mine.filter((p) => p.dept === d.id);
        if (!pages.length) return null;
        return (
          <section key={d.id} className="bg-white border rounded-xl p-4">
            <h2 className="font-bold mb-3" style={{ color: "#154E93" }}>{d.label}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {pages.map((p) => (
                <Link key={p.key} to={p.paths[0] === "/inspector" ? "/admin/inspector-access" : (p.exact?.[0] && !p.paths.length ? p.exact[0] : p.paths[0])} className="border rounded-lg p-3 hover:bg-slate-50">
                  <div className="font-semibold text-gray-800">{p.label}</div>
                  <div className="text-xs mt-1"><span className={`px-2 py-0.5 rounded-full ${access.permissions[p.key] === "edit" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>{access.permissions[p.key] === "edit" ? "Can edit" : "View only"}</span></div>
                </Link>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
